import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { useLazyGetAmlCheckQuery } from '../../../../api';
import { Table } from '../../../../components/table/table';
import { HeadCell, Row, RowCell } from '../../../../components/table/types';
import { Transaction } from '../../../../types';
import Title from '../../../../components/title/Title';

export const AmlBlock: FC<{ transaction?: Transaction }> = ({ transaction }) => {
  const { t } = useTranslation();

  const [getAmlCheck, { data: amlCheck, isLoading: isAmlCheckLoading, error: amlCheckError }] =
    useLazyGetAmlCheckQuery();

  const [activeTab, setActiveTab] = useState<number>(1);

  useEffect(() => {
    if (transaction) {
      getAmlCheck({
        blockchain: transaction.coin.blockchain,
        hash:
          transaction.transfer_info?.sender || transaction.transfer_info?.recipient_address || '',
        time: transaction.created_at,
        type: 'BLOCKCHAIN_ADDRESS',
      });
    }
  }, [transaction]);

  const headCells: HeadCell[] = [
    { id: '1', label: t('transaction.aml.accordion.entity') },
    { id: '2', label: t('transaction.aml.accordion.risk-trigger-value') },
    { id: '3', label: t('transaction.aml.accordion.closest-proximity') },
    { id: '4', label: t('transaction.aml.accordion.contribution') },
    { id: '5', label: t('transaction.aml.accordion.value-usd') },
  ];

  const sourceAccordions = amlCheck?.check?.data.evaluation_detail?.source?.map((it, index) => {
    const matchedElements = it.matched_elements?.map((item, indexMatchedElements) => {
      const { contributions, category } = item;
      const roleRows: Row[] | undefined = contributions?.map((contribution) => {
        const {
          entity,
          risk_triggers,
          min_number_of_hops,
          contribution_percentage,
          contribution_value,
        } = contribution;

        const cells: RowCell[] = [
          { name: 'entity', value: entity },
          {
            name: 'category',
            value: (
              <Box
                sx={{
                  borderRadius: '24px;',
                  padding: '8px 10px',
                  backgroundColor: '#1976d2',
                  color: '#fff',
                }}
              >
                {risk_triggers?.category}
              </Box>
            ),
          },
          { name: 'min_number_of_hops', value: min_number_of_hops },
          { name: 'contribution_percentage', value: contribution_percentage?.toFixed(2) },
          { name: 'usd', value: contribution_value?.usd },
        ];

        return { cells };
      });

      return (
        <Accordion key={indexMatchedElements}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>{category}</AccordionSummary>
          <AccordionDetails>
            <Table headCells={headCells} rows={roleRows} isLoading={isAmlCheckLoading} />
          </AccordionDetails>
        </Accordion>
      );
    });

    return (
      <Accordion key={index} defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', gap: '12px' }}>
            <Box
              sx={{
                borderRadius: '50%',
                padding: '11px 0',
                textAlign: 'center',
                height: '48px',
                width: '48px',
                border: '2px solid #1976d2',
              }}
            >
              {it?.risk_score?.toFixed(2)}
            </Box>
            {`${it?.rule_name} (${it?.matched_elements?.length})`}
          </Stack>
        </AccordionSummary>
        <AccordionDetails>{matchedElements}</AccordionDetails>
      </Accordion>
    );
  });

  const destinationAccordions = amlCheck?.check?.data.evaluation_detail?.destination?.map(
    (it, index) => {
      const matchedElements = it.matched_elements?.map((item, indexMatchedElements) => {
        const { contributions, category } = item;
        const roleRows: Row[] | undefined = contributions?.map((contribution) => {
          const {
            entity,
            risk_triggers,
            min_number_of_hops,
            contribution_percentage,
            contribution_value,
          } = contribution;

          const cells: RowCell[] = [
            { name: 'entity', value: entity },
            {
              name: 'category',
              value: (
                <Box
                  sx={{
                    borderRadius: '24px;',
                    padding: '8px 10px',
                    backgroundColor: '#1976d2',
                    color: '#fff',
                  }}
                >
                  {risk_triggers?.category}
                </Box>
              ),
            },
            { name: 'min_number_of_hops', value: min_number_of_hops },
            { name: 'contribution_percentage', value: contribution_percentage?.toFixed(2) },
            { name: 'usd', value: contribution_value?.usd },
          ];

          return { cells };
        });

        return (
          <Accordion key={indexMatchedElements}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>{category}</AccordionSummary>
            <AccordionDetails>
              <Table headCells={headCells} rows={roleRows} isLoading={isAmlCheckLoading} />
            </AccordionDetails>
          </Accordion>
        );
      });

      return (
        <Accordion key={index} defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', gap: '12px' }}>
              <Box
                sx={{
                  borderRadius: '50%',
                  padding: '11px 0',
                  textAlign: 'center',
                  height: '48px',
                  width: '48px',
                  border: '2px solid #1976d2',
                }}
              >
                {it?.risk_score?.toFixed(2)}
              </Box>
              {`${it?.rule_name} (${it?.matched_elements?.length})`}
            </Stack>
          </AccordionSummary>
          <AccordionDetails>{matchedElements}</AccordionDetails>
        </Accordion>
      );
    }
  );

  return (
    <Grid size={{ xs: 12 }} sx={{ '&.MuiGrid-item': { pt: 1.6 } }}>
      <Paper sx={{ p: '24px 16px', display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Title>{t('transaction.aml.title')}</Title>
        {amlCheckError || !amlCheck?.check ? (
          <Typography component="h4" variant="inherit" color="inherit" sx={{ flexGrow: 1 }}>
            {t('transaction.aml.emptyData')}
          </Typography>
        ) : (
          <>
            <Typography component="h4" variant="inherit" color="inherit" sx={{ flexGrow: 1 }}>
              {`${t('transaction.aml.reviewed-address')}: ${amlCheck?.check.hash}`}
            </Typography>
            <Typography
              component="h4"
              variant="inherit"
              color="inherit"
              sx={{ display: 'flex', flexDirection: 'row', gap: '4px', flexGrow: 1 }}
            >
              {`${t('transaction.aml.risk-score')}: `}
              <div style={{ fontWeight: 'bold' }}>
                {amlCheck?.check.data.risk_score
                  ? amlCheck?.check.data.risk_score
                  : t('transaction.aml.empty-risk-score')}
              </div>
            </Typography>
            <Tabs value={activeTab} variant="scrollable" scrollButtons="auto">
              <Tab
                label={t('transaction.aml.source')}
                value={1}
                onClick={() => setActiveTab(1)}
                sx={{ textTransform: 'none' }}
              />
              <Tab
                label={t('transaction.aml.destination')}
                value={2}
                onClick={() => setActiveTab(2)}
                sx={{ textTransform: 'none' }}
              />
            </Tabs>
            {activeTab === 1 && (
              <Stack direction="column" spacing={1}>
                {sourceAccordions}
              </Stack>
            )}
            {activeTab === 2 && (
              <Stack direction="column" spacing={2}>
                {destinationAccordions}
              </Stack>
            )}
          </>
        )}
      </Paper>
    </Grid>
  );
};
